/**
 * Per-user preferences, stored on the account (not this device). Reads come
 * with defaults so the UI never waits on the network to know a toggle's value;
 * writes are optimistic and land in the query cache immediately.
 */
import { fetchApi } from '@/api/client';
import { optimistic } from '@/api/optimistic';
import { queryKeys } from '@/api/queryKeys';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  USER_PREFERENCE_DEFAULTS,
  type UpdateUserPreferencesInput,
  type UserPreferences,
} from '@ultimate-checklist/shared';

const preferencesQuery = {
  queryKey: queryKeys.mePreferences,
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    fetchApi<UserPreferences>('/api/me/preferences', { signal }),
  staleTime: 5 * 60_000,
};

export function usePreferences() {
  return useQuery(preferencesQuery);
}

/** The account's preferences, fetching them if they aren't cached yet — for code outside React. */
export function ensurePreferences(qc: QueryClient): Promise<UserPreferences> {
  return qc.ensureQueryData(preferencesQuery);
}

/** One preference, with its default until the account's values have loaded. */
export function usePreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] {
  const prefs = usePreferences();
  return (prefs.data ?? USER_PREFERENCE_DEFAULTS)[key];
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateUserPreferencesInput) =>
      fetchApi<UserPreferences>('/api/me/preferences', { method: 'PATCH', body: patch }),
    ...optimistic<UpdateUserPreferencesInput>({
      qc,
      keys: () => [queryKeys.mePreferences],
      apply: (patch) =>
        qc.setQueryData<UserPreferences>(queryKeys.mePreferences, (current) => ({
          ...(current ?? USER_PREFERENCE_DEFAULTS),
          ...patch,
        })),
      errorMessage: 'Couldn’t save that setting',
    }),
  });
}
